// Customer detail page — /customers/:id
// Tabs: Thông tin | Đơn hàng | Công nợ
import { useState } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiClient } from "@/lib/api/client";
import { formatDate } from "@/lib/format/date";
import { DebtHistory } from "./components/debt-history";
import { CollectDebtDialog } from "./components/collect-debt-dialog";
import { AddDebtDialog } from "./components/add-debt-dialog";

interface Customer {
  id: string; phone: string; name: string; email: string;
  address: string; birthday?: string; gender?: string; note: string;
  totalSpent: string; orderCount: number; isActive: boolean;
  customerType?: string;
}

interface Order {
  id: string; code: string; status: string; total: string;
  paidAmount: string; createdAt: string; paymentMethod?: string;
}

function fmtVND(n: string) {
  return new Intl.NumberFormat("vi-VN").format(parseFloat(n) || 0) + " ₫";
}

type Tab = "info" | "orders" | "debt";

export function CustomerDetailPage() {
  // Use strict:false to read params without binding to a route id — matches the
  // pattern used by other detail pages (products, imports) and avoids the
  // "Could not find an active match from /customers/$id" invariant from
  // TanStack Router when the id-typed lookup misses.
  const { id } = useParams({ strict: false }) as { id: string };
  const [tab, setTab] = useState<Tab>("info");
  const [collectOpen, setCollectOpen] = useState(false);
  const [addDebtOpen, setAddDebtOpen] = useState(false);
  const qc = useQueryClient();

  // apiGet unwraps the envelope and returns T directly. Earlier code re-read
  // `data.data`, which broke when the BE returned a single object (no nested data).
  const { data: c, isLoading } = useQuery({
    queryKey: ["customer", id],
    queryFn: () => apiGet<Customer>(`/v1/customers/${id}`),
  });

  const { data: orders } = useQuery({
    queryKey: ["orders", "customer", id],
    queryFn: () => apiGet<Order[]>(`/v1/orders?customer_id=${id}&limit=20`),
    enabled: tab === "orders",
  });

  // Debt balance lives in the response envelope's `meta`, not in `data`, so we
  // call apiClient directly to retain the full envelope.
  const { data: debtMeta } = useQuery({
    queryKey: ["debts", "balance", id],
    queryFn: async () => {
      const res = await apiClient.get<{ meta: { balance: string } }>(
        `/v1/debts?customer_id=${id}&limit=1`,
      );
      return res.data;
    },
  });

  const ordersList = orders ?? [];
  const balance = debtMeta?.meta?.balance ?? "0";

  if (isLoading) return <div className="text-center py-12 text-muted-foreground">Đang tải...</div>;
  if (!c) return <div className="text-center py-12 text-destructive">Không tìm thấy khách hàng</div>;

  const tabs: { key: Tab; label: string }[] = [
    { key: "info", label: "Thông tin" },
    { key: "orders", label: "Đơn hàng" },
    { key: "debt", label: `Công nợ ${parseFloat(balance) > 0 ? `(${fmtVND(balance)})` : ""}` },
  ];

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/customers" className="text-sm text-muted-foreground hover:underline">← Danh sách</Link>
          <div className="flex items-center gap-2 mt-1">
            <h1 className="text-2xl font-bold">{c.name || c.phone}</h1>
            {c.customerType === "wholesale" && (
              <span className="rounded px-1.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700">Sỉ</span>
            )}
          </div>
          <p className="text-muted-foreground font-mono">{c.phone}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/customers/${id}/edit` as any}
            className="rounded-md border px-4 py-2 text-sm font-medium"
          >
            Chỉnh sửa
          </Link>
          {parseFloat(balance) > 0 && (
            <button
              onClick={() => setCollectOpen(true)}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Thu nợ
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Tổng chi tiêu", value: fmtVND(c.totalSpent) },
          { label: "Số đơn hàng", value: c.orderCount },
          { label: "Công nợ", value: fmtVND(balance), highlight: parseFloat(balance) > 0 },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className={`mt-1 text-xl font-bold ${s.highlight ? "text-destructive" : ""}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <dl className="grid grid-cols-2 gap-4 text-sm">
          {[
            ["Email", c.email || "—"],
            ["Địa chỉ", c.address || "—"],
            ["Ngày sinh", c.birthday || "—"],
            ["Giới tính", c.gender || "—"],
            ["Loại khách hàng", c.customerType === "wholesale" ? "Sỉ" : "Lẻ"],
            ["Ghi chú", c.note || "—"],
            ["Trạng thái", c.isActive ? "Hoạt động" : "Ngừng"],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="font-medium">{v}</dd>
            </div>
          ))}
        </dl>
      )}

      {tab === "orders" && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50">
              <tr>
                {["Mã đơn", "Ngày", "Tổng", "Đã trả", "Trạng thái"].map((h) => (
                  <th key={h} className="px-4 py-2 text-left font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordersList.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Chưa có đơn hàng</td></tr>
              )}
              {ordersList.map((o) => (
                <tr key={o.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-mono">
                    <Link to={`/orders/${o.id}` as any} className="text-primary hover:underline">{o.code}</Link>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{formatDate(o.createdAt)}</td>
                  <td className="px-4 py-2">{fmtVND(o.total)}</td>
                  <td className="px-4 py-2">{fmtVND(o.paidAmount)}</td>
                  <td className="px-4 py-2">{o.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "debt" && (
        <DebtHistory
          customerId={id}
          balance={balance}
          onCollect={() => setCollectOpen(true)}
          onAddDebt={() => setAddDebtOpen(true)}
        />
      )}

      <AddDebtDialog
        open={addDebtOpen}
        customerId={id}
        customerName={c.name || c.phone}
        onClose={() => setAddDebtOpen(false)}
        onSuccess={() => {
          setAddDebtOpen(false);
          qc.invalidateQueries({ queryKey: ["debts", "balance", id] });
          qc.invalidateQueries({ queryKey: ["debts", id] });
          qc.invalidateQueries({ queryKey: ["debts", "customers"] });
        }}
      />

      <CollectDebtDialog
        open={collectOpen}
        customerId={id}
        customerName={c.name || c.phone}
        onClose={() => setCollectOpen(false)}
        onSuccess={() => {
          setCollectOpen(false);
          qc.invalidateQueries({ queryKey: ["debts", "balance", id] });
          qc.invalidateQueries({ queryKey: ["debts", id] });
        }}
      />
    </div>
  );
}
